import {
    Box,
    CardActionArea,
    Dialog,
    DialogContent,
    DialogTitle,
    Stack,
    Typography as MuiTypography,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import {
    FoodsIcon,
    FoodsIconButton,
    SelectionCardGroup,
    type SelectionCardOption,
    Typography as FoodsTypography,
} from "@foodpilot/foods";
import type { Dispatch, SetStateAction } from "react";
import { useTranslation } from "react-i18next";
import { FoodsBottomSheetDialog } from "src/components/Dialog/FoodsBottomSheetDialog";

type TransportCreateChoiceModalProps = {
    appearance?: "legacy" | "product-v2";
    open: boolean;
    onClose: () => void;
    onSelectLeg: () => void;
    onSelectItinerary: () => void;
};

type TransportChoice = "leg" | "itinerary";

type LegacyChoiceCardProps = {
    icon: "leg" | "route";
    title: string;
    description: string;
    onClick: () => void;
};

export const TransportCreateChoiceModal = ({
    appearance = "product-v2",
    open,
    onClose,
    onSelectLeg,
    onSelectItinerary,
}: TransportCreateChoiceModalProps) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const isNarrow = useMediaQuery(theme.breakpoints.down("md"));
    const options: SelectionCardOption<TransportChoice>[] = [
        {
            id: "leg",
            icon: (
                <Box sx={{ color: theme.palette.primary[600] }}>
                    <FoodsIcon icon="leg" size={4} placeholder="defaultIcon" />
                </Box>
            ),
            title: t("transport.create_choice.leg.title", "Leg"),
            description: t(
                "transport.create_choice.leg.description",
                "A single point-to-point segment using one transport mode.",
            ),
        },
        {
            id: "itinerary",
            icon: (
                <Box sx={{ color: theme.palette.primary[600] }}>
                    <FoodsIcon icon="route" size={4} placeholder="defaultIcon" />
                </Box>
            ),
            title: t("transport.create_choice.itinerary.title", "Itinerary"),
            description: t(
                "transport.create_choice.itinerary.description",
                "A combination of legs that describes a full multi-modal journey.",
            ),
        },
    ];

    const selectTransportType = (choice: TransportChoice) => {
        if (choice === "leg") {
            onSelectLeg();
            return;
        }

        onSelectItinerary();
    };

    const setOpen: Dispatch<SetStateAction<boolean>> = (nextOpen) => {
        const isOpen = typeof nextOpen === "function" ? nextOpen(open) : nextOpen;

        if (!isOpen) onClose();
    };

    if (appearance === "legacy") {
        return (
            <Dialog
                data-appearance="legacy"
                open={open}
                onClose={onClose}
                maxWidth="md"
                fullWidth
                PaperProps={{
                    "data-appearance": "legacy",
                    sx: {
                        maxWidth: theme.spacing(92),
                        borderRadius: theme.spacing(1.5),
                        backgroundColor: theme.custom.grey[100],
                    },
                }}
            >
                <DialogTitle
                    component="div"
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        minHeight: theme.spacing(8),
                        p: theme.spacing(2, 2, 2, 3),
                        borderBottomColor: theme.custom.grey[500],
                        borderBottomStyle: "solid",
                        borderBottomWidth: 1,
                    }}
                >
                    <MuiTypography component="h2" variant="h5">
                        {t("transport.create_choice.title", "Create a transport")}
                    </MuiTypography>
                    <FoodsIconButton
                        size="m"
                        icon="close"
                        tooltip={{ text: t("common.close", "Close") }}
                        buttonProps={{ onClick: onClose, "aria-label": t("common.close", "Close") }}
                    />
                </DialogTitle>

                <DialogContent sx={{ p: 3 }}>
                    <Stack gap={2}>
                        <MuiTypography variant="body1" sx={{ fontWeight: 500 }}>
                            {t("transport.create_choice.question", "What kind of transport do you want to create?")}
                        </MuiTypography>

                        <Stack direction={{ xs: "column", md: "row" }} gap={3}>
                            <LegacyChoiceCard
                                icon="leg"
                                title={t("transport.create_choice.leg.title", "Leg")}
                                description={t(
                                    "transport.create_choice.leg.description",
                                    "A single point-to-point segment using one transport mode.",
                                )}
                                onClick={onSelectLeg}
                            />
                            <LegacyChoiceCard
                                icon="route"
                                title={t("transport.create_choice.itinerary.title", "Itinerary")}
                                description={t(
                                    "transport.create_choice.itinerary.description",
                                    "A combination of legs that describes a full multi-modal journey.",
                                )}
                                onClick={onSelectItinerary}
                            />
                        </Stack>
                    </Stack>
                </DialogContent>
            </Dialog>
        );
    }

    return (
        <FoodsBottomSheetDialog
            open={open}
            PaperProps={{
                sx: {
                    maxWidth: theme.spacing(92),
                    "& > .MuiDialogTitle-root": {
                        minHeight: theme.spacing(9.5),
                        p: 2,
                    },
                },
            }}
            setOpen={setOpen}
            title={
                <FoodsTypography id="transport-create-choice-title" component="h2" variant="h5">
                    {t("transport.create_choice.title", "Create a transport")}
                </FoodsTypography>
            }
            content={
                <Box sx={{ p: 3 }}>
                    <Stack
                        gap={2}
                        sx={{
                            "& > [role='radiogroup'] > [role='radio']": {
                                height: theme.spacing(11.5),
                                minHeight: theme.spacing(11.5),
                                alignItems: "flex-start",
                            },
                        }}
                    >
                        <FoodsTypography variant="body-medium">
                            {t("transport.create_choice.question", "What kind of transport do you want to create?")}
                        </FoodsTypography>

                        <SelectionCardGroup<TransportChoice>
                            direction={isNarrow ? "column" : "row"}
                            fullWidth
                            name={t("transport.create_choice.title", "Create a transport")}
                            onChange={selectTransportType}
                            options={options}
                        />
                    </Stack>
                </Box>
            }
        />
    );
};

const LegacyChoiceCard = ({ icon, title, description, onClick }: LegacyChoiceCardProps) => {
    const theme = useTheme();

    return (
        <Box
            sx={{
                flex: 1,
                borderColor: theme.palette.primary[200],
                borderStyle: "solid",
                borderWidth: 1,
                borderRadius: theme.spacing(1.5),
                backgroundColor: theme.custom.grey[100],
                boxShadow: theme.custom.shadows.grey,
                overflow: "hidden",
            }}
        >
            <CardActionArea onClick={onClick} sx={{ p: 2, height: "100%" }}>
                <Stack direction="row" gap={2} alignItems="center">
                    <Box
                        sx={{
                            width: theme.spacing(5.5),
                            height: theme.spacing(5.5),
                            display: "grid",
                            placeItems: "center",
                            flexShrink: 0,
                            borderRadius: theme.spacing(1.5),
                            backgroundColor: theme.palette.primary[50],
                            color: theme.palette.primary[500],
                        }}
                    >
                        <FoodsIcon icon={icon} size={3.5} placeholder="defaultIcon" />
                    </Box>
                    <Stack gap={0.5}>
                        <MuiTypography variant="body1" sx={{ fontWeight: 600 }}>
                            {title}
                        </MuiTypography>
                        <MuiTypography variant="body2" sx={{ color: theme.custom.grey[2800] }}>
                            {description}
                        </MuiTypography>
                    </Stack>
                </Stack>
            </CardActionArea>
        </Box>
    );
};
